import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, MapPin, Heart, Camera, AlertTriangle, Coffee } from 'lucide-react';
import './GuidePage.css';

export default function NC500Story() {
  return (
    <div className="guide-page">
      <Link to="/wissen" className="back-link"><ArrowLeft size={20} /> Zurück zum Wissen</Link>
      
      <article className="guide-content">
        <h1>📸 NC500 in 10 Tagen - Sarah's Erfahrungsbericht</h1>
        
        <div className="info-box">
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
            <div>
              <User size={24} />
            </div>
            <div>
              <p><strong>Autorin:</strong> Sarah M., 34 Jahre</p>
              <p><strong>Tour:</strong> North Coast 500 (NC500)</p>
              <p><strong>Dauer:</strong> 10 Tage (September 2024)</p>
              <p><strong>Distanz:</strong> 850 km, ~9.500 Höhenmeter</p>
              <p><strong>Rad:</strong> E-Bike (Cannondale Synapse NEO, 75Nm Motor)</p>
            </div>
          </div>
        </div>

        <section>
          <h2>Warum NC500?</h2>
          <p>
            Als ich vor einem Jahr beschloss, die NC500 zu fahren, wusste ich: Das wird kein 
            Spaziergang. <strong>850 Kilometer, 9.000+ Höhenmeter, oft einsame Single-Track Roads, 
            unberechenbares schottisches Wetter.</strong> Aber genau das reizte mich!
          </p>
          <p>
            Ich bin keine Profi-Radfahrerin - eher durchschnittliche Fitness, aber ich liebe 
            Abenteuer. Ein E-Bike war für mich die richtige Wahl. Ich wollte die Landschaft 
            genießen, nicht nur leiden.
          </p>
        </section>

        <section>
          <h2>Vorbereitung</h2>
          
          <h3>6 Monate vorher</h3>
          <ul>
            <li><strong>Unterkünfte gebucht:</strong> Mai-September = NC500 ist VOLL! Ich buchte 
            6 Monate im Voraus. Trotzdem: In Lochinver nur noch ein Zimmer frei gefunden.</li>
            <li><strong>E-Bike gekauft:</strong> Cannondale Synapse NEO mit 75Nm Motor. Wichtig: 
            Reichweite 100km+ (mit Eco-Modus 130km möglich).</li>
            <li><strong>Training:</strong> 3x pro Woche, 40-60km Touren. Fokus: Anstiege!</li>
          </ul>

          <h3>1 Monat vorher</h3>
          <ul>
            <li><strong>Ausrüstung getestet:</strong> Regenjacke, Packtaschen, Ladegerät (UK-Adapter!)</li>
            <li><strong>Route geplant:</strong> 10 Etappen, Uhrzeigersinn (Westküste zuerst = empfohlen)</li>
            <li><strong>Notfall-Kontakte:</strong> Familie informiert, Spot-Tracker organisiert</li>
          </ul>

          <div className="pro-tip">
            <h3>💡 Mein größter Tipp</h3>
            <p>
              <strong>September statt Juli/August!</strong> Weniger Touristen, Midges fast weg, 
              Unterkünfte verfügbar. Ja, etwas mehr Regen - aber das ist Schottland, Regen 
              kriegst du immer! 😅
            </p>
          </div>
        </section>

        <section>
          <h2>Tag 1: Inverness → Ullapool (95km, 1.200 HM)</h2>
          
          <p>
            <strong>Reality Check:</strong> Der erste Tag war härter als erwartet. Die A835 ist 
            wellig, und "1.200 Höhenmeter" fühlten sich an wie 2.000. Aber: <strong>Corrieshalloch 
            Gorge</strong> war atemberaubend! (Abstecher 5km, lohnt sich!)
          </p>
          
          <div className="checklist">
            <div className="check-item">✅ Start 7:30 Uhr (Verkehr vermieden)</div>
            <div className="check-item">✅ Picknick an Loch Glascarnoch (traumhaft!)</div>
            <div className="check-item">❌ Zu viel Gepäck (10kg - next time: 7kg max)</div>
            <div className="check-item">✅ Ullapool: Fish & Chips am Hafen (verdient!)</div>
          </div>

          <p><strong>Übernachtung:</strong> The Ceilidh Place (empfehlenswert, zentral, Rad-sicher)</p>
        </section>

        <section>
          <h2>Tag 2: Ullapool → Lochinver (60km, 800 HM)</h2>
          
          <p>
            Heute begann die "richtige" NC500: <strong>Westküste = spektakulär!</strong> Die 
            Assynt Mountains sind einfach... wow. Stac Pollaidh sieht aus wie aus einem Fantasy-Film.
          </p>

          <div className="warning-box">
            <AlertTriangle size={20} />
            <strong>Single-Track Reality:</strong> Ich musste lernen, dass Single-Track Roads 
            ernst gemeint sind. 5-6 Mal von Autos überholt, jedes Mal rechts ranfahren, 
            Hand heben, danken. Es ist ein Tanz - aber die Autofahrer waren alle freundlich!
          </div>

          <p><strong>Highlight:</strong> Ardvreck Castle Ruine am Loch Assynt - Fotostopp Pflicht!</p>
          <p><strong>Übernachtung:</strong> Inver Lodge Hotel (teuer, aber fantastischer Blick)</p>
        </section>

        <section>
          <h2>Tag 3: Lochinver → Scourie (50km, 600 HM)</h2>
          
          <p>
            <strong>Kürzeste Etappe, aber härteste!</strong> Die B869 Küstenstraße ist... brutal. 
            Nicht wegen der Länge, sondern wegen der <strong>ständigen Auf-und-Abs</strong>. 
            10 Mal hoch, 10 Mal runter. Aber jede Kurve = neue spektakuläre Aussicht!
          </p>

          <div className="pro-tip">
            <h3><Coffee size={20} /> Drumbeg Loop</h3>
            <p>
              Optional-Schleife zur Drumbeg. Ich hab's gemacht (+10km). <strong>Lohnt sich SO sehr!</strong> 
              Einsame Buchten, türkisblaues Wasser (ja, in Schottland!), ein Pub mit Torffeuer.
            </p>
          </div>

          <p><strong>Realisation:</strong> E-Bike-Akku bei 25% am Ziel. Ich hatte Panik, aber 
          Scourie Hotel hatte Steckdose. Lerneffekt: Eco-Modus konsequenter nutzen!</p>
        </section>

        <section>
          <h2>Tag 4: Scourie → Durness (60km, 700 HM)</h2>
          
          <p>
            Kylesku Bridge = ikonisch! Danach: <strong>Smoo Cave</strong> bei Durness - riesige 
            Höhle direkt am Meer (kostenlos, kurze Wanderung). Mind. blown! 🤯
          </p>

          <p>
            <strong>Durness</strong> selbst ist winzig. Aber: Nördlichster Punkt des Mainlands! 
            (Cape Wrath nur mit Fähre + Minibus erreichbar - ich hab's ausgelassen, zu umständlich)
          </p>

          <p><strong>Übernachtung:</strong> Smoo Cave Hotel (B&B, super Frühstück)</p>
        </section>

        <section>
          <h2>Tag 5: Durness → Tongue (50km, 600 HM)</h2>
          
          <p>
            Entspannteste Etappe! A838 Nordküste ist <strong>flacher</strong> als Westküste. 
            Endlich mal durchatmen! 😅
          </p>

          <div className="checklist">
            <div className="check-item"><Camera /> Kyle of Durness Panorama (Foto-Spot!)</div>
            <div className="check-item"><Camera /> Balnakeil Beach (weißer Sand, leer!)</div>
            <div className="check-item"><Camera /> Tongue: Blick auf Ben Loyal (majestätisch)</div>
          </div>

          <p><strong>Wind-Warnung:</strong> Heute hatte ich Rückenwind (Glück!). Aber ich sah 
          andere Radfahrer gegen starken Westwind kämpfen - das kann brutal sein!</p>
        </section>

        <section>
          <h2>Tag 6: Tongue → Thurso (80km, 800 HM)</h2>
          
          <p>
            Längere Etappe, aber machbar. <strong>Bettyhill</strong> = hübsches Dorf, Strathy Point 
            = wilde Klippen. Die Nordküste fühlt sich anders an als Westküste - rauer, windiger, 
            baumloses Moorland.
          </p>

          <p>
            <strong>Thurso</strong> ist die erste "richtige" Stadt seit Ullapool! Supermarkt! 
            Fish & Chips! Ich war SO happy! 😂
          </p>

          <p><strong>Übernachtung:</strong> Thurso (mehrere Hotels, ich wählte The Castletown)</p>
        </section>

        <section>
          <h2>Tag 7: Thurso → John o' Groats → Wick (70km, 600 HM)</h2>
          
          <p>
            <strong>John o' Groats</strong> = touristisches Ziel, aber hey: Nördlichster Punkt! 
            Ich musste das Foto machen! 📸 (Ja, es ist touristisch, ja, es gibt wenig außer dem 
            Schild, aber es gehört dazu!)
          </p>

          <div className="pro-tip">
            <h3><MapPin size={20} /> Duncansby Stacks</h3>
            <p>
              3km östlich von John o' Groats: <strong>Duncansby Head & Stacks</strong>. 
              DRAMATISCHE Klippen, Felsnadeln im Meer, Seevögel. <strong>DAS war das echte 
              Highlight, nicht John o' Groats selbst!</strong>
            </p>
          </div>

          <p><strong>Wick:</strong> Industrielle Hafenstadt, nicht schön, aber praktisch. Gute 
          Fish & Chips, günstiges B&B.</p>
        </section>

        <section>
          <h2>Tag 8: Wick → Helmsdale (50km, 600 HM)</h2>
          
          <p>
            <strong>A9 = Verkehr!</strong> Nach 7 Tagen einsamer Single-Tracks fühlte sich die 
            A9 wie eine Autobahn an. LKWs, Autos, Wohnmobile. Aber: Guter Seitenstreifen, und 
            es geht schnell voran.
          </p>

          <p>
            <strong>Helmsdale:</strong> Winziges Fischerdorf, charmant. La Mirage Restaurant = 
            skurril aber lecker (Seafood!).
          </p>
        </section>

        <section>
          <h2>Tag 9: Helmsdale → Dornoch (70km, 900 HM)</h2>
          
          <p>
            <strong>Dunrobin Castle</strong> = Pflicht-Stopp! Sieht aus wie französisches Schloss. 
            Gärten sind wunderschön (Entry £13, lohnt sich).
          </p>

          <p>
            <strong>Dornoch:</strong> Hübsche Kleinstadt, berühmter Golfplatz, schöne Kathedrale. 
            Ich hatte Rest Day verdient - nur 3h Radeln, dann Entspannung.
          </p>

          <p><strong>Übernachtung:</strong> 2 Nächte Dornoch (Rest Day!)</p>
        </section>

        <section>
          <h2>Tag 10: Dornoch → Inverness (90km, 800 HM)</h2>
          
          <p>
            <strong>Final Push!</strong> Via Black Isle (optional Schleife - ich hab's gemacht, 
            +20km aber lohnt sich!). <strong>Chanonry Point:</strong> Dolphins watching! Ich sah 
            tatsächlich 2 Dolphins! 🐬
          </p>

          <p>
            <strong>Zurück in Inverness:</strong> Emotionaler Moment. 850km, 10 Tage, ich hab's 
            geschafft! Selbst mit E-Bike war es eine Challenge. Aber WOW, was für eine Erfahrung!
          </p>
        </section>

        <section>
          <h2>Was ich gelernt habe</h2>
          
          <h3><Heart size={20} /> Die 10 wichtigsten Learnings</h3>
          <ol>
            <li><strong>E-Bike = beste Entscheidung!</strong> Ohne Motor hätte ich die Anstiege 
            gehasst. Mit Motor: genießbar!</li>
            <li><strong>Akku-Reichweite planen!</strong> Eco-Modus reicht meist. Turbo nur für 
            steile Rampen. Jede Unterkunft fragen: "Can I charge my bike?"</li>
            <li><strong>Weniger Gepäck!</strong> Ich hatte 10kg, next time: 7kg max. Jedes Gramm 
            zählt bei Anstiegen.</li>
            <li><strong>Früh starten!</strong> 7-8 Uhr los = weniger Verkehr, besseres Licht, mehr 
            Zeit für Pausen.</li>
            <li><strong>Single-Track Etiquette lernen!</strong> Rechts ran, Autos vorbeilassen, 
            Hand heben = Highland-Höflichkeit.</li>
            <li><strong>Midges im Griff haben!</strong> Midge-Netz + Smidge-Spray = Lebensretter! 
            (September war OK, aber Juli/August = Hölle laut anderen Radfahrern)</li>
            <li><strong>Regenkleidung IMMER griffbereit!</strong> Wetter wechselt in 10 Minuten. 
            4 Jahreszeiten an einem Tag = kein Witz.</li>
            <li><strong>Snacks mitnehmen!</strong> Zwischen Ullapool und Thurso: Fast nichts! 
            Ich hatte immer 3-4 Energieriegel + Nüsse dabei.</li>
            <li><strong>Flexibel bleiben!</strong> Tag 4 war windig - ich hab Pause gemacht statt 
            zu kämpfen. Tag 6 war perfekt - ich bin weitergefahren als geplant.</li>
            <li><strong>Genießen!</strong> Es geht nicht um Geschwindigkeit, sondern um die 
            Erfahrung. Pausen machen, Fotos schießen, mit Locals reden!</li>
          </ol>
        </section>

        <section>
          <h2>Kosten</h2>
          
          <div className="data-grid">
            <div className="data-item">
              <strong>Unterkünfte (10 Nächte):</strong> £850 (B&Bs, mid-range)
            </div>
            <div className="data-item">
              <strong>Essen:</strong> £400 (Restaurants + Snacks)
            </div>
            <div className="data-item">
              <strong>Flug + Bike-Transport:</strong> £350 (München - Inverness)
            </div>
            <div className="data-item">
              <strong>Sonstiges:</strong> £150 (Museen, Dunrobin Castle, etc.)
            </div>
          </div>
          <p><strong>Gesamt:</strong> ~£1.750 (~€2.000) für 10 Tage</p>
        </section>

        <section>
          <h2>Würde ich es nochmal machen?</h2>
          
          <p>
            <strong>JA! Sofort!</strong> 🎉
          </p>
          <p>
            Die NC500 war die beste Radtour meines Lebens. Ja, es war anstrengend. Ja, ich hatte 
            Momente, wo ich dachte "Warum tue ich mir das an?" (Tag 3, Kilometer 40, steiler 
            Anstieg, Regen...). Aber: <strong>Die Landschaft, die Einsamkeit, das Gefühl von 
            Abenteuer - unbezahlbar!</strong>
          </p>
          <p>
            Für mich war das E-Bike der Schlüssel. Ohne hätte ich es nicht genossen, sondern nur 
            gelitten. Mit E-Bike: Ich konnte die Landschaft aufsaugen, Fotos machen, mit Schafen 
            quatschen (ja, wirklich! 😄), und trotzdem 80-90km/Tag schaffen.
          </p>
          <p>
            <strong>Meine Message:</strong> Du musst kein Profi sein. Du brauchst kein 
            Highend-Rennrad. Ein E-Bike, realistische Planung, und die richtige Einstellung reichen! 
            Die NC500 ist für alle machbar - trau dich!
          </p>
        </section>

        <div className="pro-tip">
          <h3>🎯 Sarah's Top 5 Momente</h3>
          <ol>
            <li><strong>Drumbeg B869:</strong> Küstenstraße mit türkisblauen Buchten (Tag 3)</li>
            <li><strong>Stac Pollaidh Aussicht:</strong> Assynt Mountains Panorama (Tag 2)</li>
            <li><strong>Duncansby Stacks:</strong> Dramatische Klippen (Tag 7)</li>
            <li><strong>Dolphins in Chanonry Point:</strong> 2 Dolphins gesehen! (Tag 10)</li>
            <li><strong>Ziel in Inverness:</strong> 850km geschafft - unbeschreibliches Gefühl!</li>
          </ol>
        </div>

        <div className="info-box">
          <h4>📚 Hilfreiche Guides</h4>
          <ul>
            <li><Link to="/wissen/region/highlands">🏔️ Highlands-Guide</Link> - NC500 Etappenplan</li>
            <li><Link to="/wissen/ausruestung">🎒 Ausrüstung</Link> - Was du wirklich brauchst</li>
            <li><Link to="/wissen/ebike">⚡ E-Bike Basics</Link> - Akku-Management</li>
          </ul>
        </div>
      </article>
    </div>
  );
}
